// =============================================================================
// refund-math — pure money math for cancellations, reductions and refunds
// =============================================================================
// Kept dependency-free and unit-tested because these amounts are bug-prone and
// must agree with the server:
//   • proratedPriceCents / reductionCreditCents mirror the SQL RPC
//     booking.fn_admin_reduce_departure_party (new price = round(total*new/old)).
//   • cappedRefundableCents / scopedRefundableCents mirror booking-cancel-refund
//     (refundable = min(departure value, amount paid); scope='booking' refunds
//     the whole payment).
// Amounts are integer cents throughout.
// =============================================================================

/**
 * Recorded price of a pick after reducing its party from oldParty → newParty,
 * prorated from the original recorded total. Mirrors the RPC's
 * `round(total * new / old)` so the UI estimate matches what the server stores.
 */
export function proratedPriceCents(
  oldTotalCents: number,
  oldParty: number,
  newParty: number,
): number {
  if (oldParty <= 0) return 0;
  return Math.round((oldTotalCents * newParty) / oldParty);
}

/**
 * Store-credit (tegoedbon) value of the seats removed by a reduction —
 * the difference between the old recorded total and the prorated new total.
 */
export function reductionCreditCents(
  oldTotalCents: number,
  oldParty: number,
  newParty: number,
): number {
  return Math.max(
    0,
    oldTotalCents - proratedPriceCents(oldTotalCents, oldParty, newParty),
  );
}

/**
 * Amount refundable for a single departure: its current value, never more than
 * was actually paid. After a party reduction the value is below the paid amount
 * (the difference already came back as a tegoedbon); for one departure of a
 * multi-departure booking the value is a fraction of the whole payment.
 * `paidCents` null → no payment on file, so fall back to the value itself.
 */
export function cappedRefundableCents(
  currentValueCents: number,
  paidCents: number | null,
): number {
  if (paidCents == null) return Math.max(0, currentValueCents);
  return Math.max(0, Math.min(currentValueCents, paidCents));
}

/**
 * Refundable amount for the chosen cancel scope. 'booking' refunds the whole
 * payment (every departure under the parent); 'departure' refunds just this
 * departure's capped value.
 */
export function scopedRefundableCents(
  scope: 'departure' | 'booking',
  departureRefundableCents: number,
  wholeBookingCents: number,
): number {
  return scope === 'booking' ? wholeBookingCents : departureRefundableCents;
}
